import { withPrefix, navigate } from 'gatsby';
import React, { useEffect, useRef, useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import TopNavigation from '@vfuk/core-top-navigation';
import { Link } from '@vfuk/core-top-navigation/dist/TopNavigation.types';
import { InteractionEvent } from '@vfuk/core-interaction';
import { addLinkTestId } from '../../helpers/addTestId';
import uniqueId from '../../helpers/uniqueId';
import {
  LinkProps,
  NavigationCombinedData,
  NavigationProps,
  StyledNavigationProps,
} from './types/Navigation.types';
import { EgyptianNavigationWrapper, NavigationWrapper } from './styles/Navigation.styles';

const MOBILE_NAV_TRIGGER = "mobile-nav-trigger";
const CLOSE_BUTTON = "close-button";

const StyledNavigation = ({
  children,
  isEgyptianSite,
  selectedIndex,
}: StyledNavigationProps): JSX.Element => {
  if (isEgyptianSite) {
    return (
      <NavigationWrapper top="-27px">
        <EgyptianNavigationWrapper selectedIndex={selectedIndex}>
          {children as JSX.Element}
        </EgyptianNavigationWrapper>
      </NavigationWrapper>
    );
  }

  return (
    <NavigationWrapper top="-34px">
      {children as JSX.Element}
    </NavigationWrapper>
  );
};

export const PureNavigation: React.FC<LinkProps> = ({
  mainLinks,
  topLevelLinks,
  languageLink,
  brandLink,
  signInLabel,
  signInUrl,
  isEgyptianSite,
  pageBuilderSlugList,
  secondLanguageUrlPrefix,
  isSecondLanguageOnUrl
}) => {
  const [pageTitle, setPageTitle] = useState<string>('');
  const [scrollDisabled, setScrollDisabled] = useState<boolean>(false);
  const sideMenuRef = useRef<Element | null>(null);
  const [sideMenuObserver, setSideMenuObserver] = useState<MutationObserver | null>(null);

  const runNavigationEventData = async (sData: Record<string, any>) => {
    if ((window as any).onNavigationUserEvent && sData) {
      try {
        const finished = await (window as any).onNavigationUserEvent('header', {
          ...sData,
        });
      } catch (e) {
        console.log(
          'onNavigationUserEvent failed - please check the Navigation SEO script JSON and that a SeoInteractionScript is correctly set up.'
        );
      }
    }
  };

  const insertStringAt = (array: string[], targetIndex: number, targetItem: string) => {
    return [...array.slice(0, targetIndex), targetItem, ...array.slice(targetIndex)];
  }

  const addSlashIfMissing = (target: string) => {
    if (target.charAt(target.length - 1) !== '/') {
        return target + '/';
    }
    return target;
  }

  const onChangeLanguage = () => {
    const pathnameArray = window.location.pathname.split('/');
    if (isSecondLanguageOnUrl) {
      // navigate didn't work as expected in the testing env (that's why I used window.location.href instead)
      window.location.href = pathnameArray.filter(item => item !== secondLanguageUrlPrefix).join('/') || '/';
    } else {
      const foundSlugIndex = pathnameArray.findIndex(item => pageBuilderSlugList.includes(item));
      if (foundSlugIndex === -1) { // homepage
        window.location.href = addSlashIfMissing(window.location.pathname) + secondLanguageUrlPrefix;
      } else {
        const convertedPathnameArray = insertStringAt(pathnameArray, foundSlugIndex, secondLanguageUrlPrefix || '');
        window.location.href = convertedPathnameArray.join('/');
      }
    }
  };

  const onClickHandler = async (e: any, href: string) => {
    if (e) {
      e.preventDefault();
    }

    await runNavigationEventData({ location: 'header' });
    window.location.href = href;
  };

  const mainNav = mainLinks.map(mainLink => ({
    id: mainLink.contentful_id,
    text: mainLink.name,
    dataAttributes: {
      title: mainLink.name,
      'data-analytics-element': uniqueId(mainLink.name),
      'data-analytics-context': pageTitle,
      'data-analytics-category': 'Link',
      'data-testid': addLinkTestId('navLink', mainLink.name),
    },
  }));

  const topNavLeftLinks: Link[] = topLevelLinks.map(topLink => ({
    id: topLink.contentful_id,
    text: topLink.name,
    onClick: () => onClickHandler(undefined, withPrefix(`${topLink.url}`)),
    active: topLink.isSelected,
    dataAttributes: {
      title: topLink.name,
      'data-analytics-element': uniqueId(topLink.name),
      'data-analytics-context': pageTitle,
      'data-analytics-category': 'Link',
      'data-testid': addLinkTestId('topLevelLink', topLink.name),
    },
  }));

  const topNavRightLinks = languageLink && languageLink.name !== "no_name" ? [{
    id: languageLink.contentful_id,
    text: languageLink.name,
    onClick: onChangeLanguage,
    dataAttributes: {
      title: languageLink.name,
      'data-analytics-element': uniqueId(languageLink.name),
      'data-analytics-context': pageTitle,
      'data-analytics-category': 'Link',
      'data-testid': addLinkTestId('topLevelLink', languageLink.name),
    }
  }] : [];

  const secondaryNavItems =
    signInLabel && signInUrl
      ? {
          secondaryNav: {
            user: {
              id: 'user-iconUserMenu',
              srText: signInLabel,
              login: {
                id: 'loginUserMenu',
                text: signInLabel,
                href: signInUrl,
              },
              logout: {
                id: 'logoutUserMenu',
                text: 'Log out',
              },
            },
          },
        }
      : null;

  const logoBrand: Link = {
    id: 'logo',
    text: 'Vodafone-logo',
    onClick: () => onClickHandler(undefined, withPrefix(`${brandLink}`)),
    dataAttributes: {
      title: 'vodafone-icon',
      'data-analytics-element': uniqueId('vodafone-icon'),
      'data-analytics-context': pageTitle,
      'data-analytics-category': 'Link',
      'data-testid': 'vodafone-icon',
    },
  };

  useEffect(() => {
    setTimeout(() => {
      // TODO: This two lines below is needed because the title of the html on the "/" path is not set. When this is fixed, then they can be removed.
      let metaName = document
        .querySelector('meta[property="og:title"]')
        ?.getAttribute('content');
      metaName = metaName ? metaName : 'Not set';
      setPageTitle(document.title.length == 0 ? metaName : document.title);
    }, 500);
  }, []);

  useEffect(() => {
    const accordionLinks = document.querySelectorAll('.chevron--inline');
    (accordionLinks as any).forEach((link: any) => {
      link.onclick = (e: any) => {
        onClickHandler(e, link.href);
      };
    });
  }, []);

  useEffect(() => {
    if (scrollDisabled) {
      initiateSideMenuTracking();
      document.body.classList.add('disable-scrolling');
    } else {
      document.body.classList.remove('disable-scrolling');
    }

    return () => {
      document.body.classList.remove('disable-scrolling');
    };
  }, [scrollDisabled]);

  const handleMutation = (sideMenu: Element) => {
    if (sideMenu.className.split(' ').includes('animation-container-exit-done')) {
      setScrollDisabled(false);
    }
  }

  useEffect(() => {
    if (sideMenuObserver && sideMenuRef.current) {
      sideMenuObserver.observe(sideMenuRef.current, { childList: true, attributes: true });
    }

    return () => {
      if (sideMenuObserver) {
        sideMenuObserver.disconnect();
      }
    };
  }, [sideMenuObserver]);

  const initiateSideMenuTracking = () => {
    const sideMenu = document.querySelector('[class^="SideNavigationstyle__SideNavigation-sc"]');

    if (sideMenu) {
      sideMenuRef.current = sideMenu;
      setSideMenuObserver(new MutationObserver(() => handleMutation(sideMenu)));
    }
  }

  const handleNavigationClick = (e: InteractionEvent, trigger: string) => {
    setScrollDisabled(trigger === MOBILE_NAV_TRIGGER);

    // TODO: delete the following lines and move onClickHandler to mainNav when TopNavigation is fixed (primaryNav/links/onClick doesn't work at the moment)
    const filteredLinks = mainLinks.filter(link => link.name === (e.target as HTMLElement).innerText);
    if (filteredLinks.length === 1) {
      onClickHandler(e, withPrefix(`${filteredLinks[0].url}`));
    }
  };

  return (
    <StyledNavigation
      isEgyptianSite={isEgyptianSite}
      selectedIndex={topLevelLinks.findIndex(topLink => topLink.isSelected) + 1}
    >
      <TopNavigation
        onClick={handleNavigationClick}
        topNav={{
          srText: 'Secondary navigation',
          leftLinks: topNavLeftLinks,
          rightLinks: topNavRightLinks
        }}
        primaryNav={{
          srText: 'Primary navigation',
          links: mainNav,
        }}
        logo={logoBrand}
        mobileNavTrigger={{
          id: MOBILE_NAV_TRIGGER,
          text: '',
        }}
        sideNavigationCloseButton={{
          id: CLOSE_BUTTON,
          srText: 'Close side navigation',
        }}
        skipLink={{
          text: 'Skip to main content',
          skipLinkId: '#main',
        }}
        dataSelectorPrefix="top-navigation"
        zIndex={99}
        {...secondaryNavItems}
      />
    </StyledNavigation>
  );
};

const Navigation = ({ firstLanguageLocalCode, secondLanguageUrlPrefix, isSecondLanguageOnUrl }: NavigationProps) => {
  const {
    allContentfulNavigation,
    allContentfulPageBuilder
  } = useStaticQuery<NavigationCombinedData>(graphql`
    {
      allContentfulNavigation {
        nodes {
          node_locale
          brandLink
          signInLabel
          signInUrl
          mainLinks {
            url
            name
            contentful_id
          }
          topLevelLinks {
            url
            name
            contentful_id
            isSelected
          }
          languageLink {
            name
          }
          isEgyptianSite
        }
      }
    
      allContentfulPageBuilder {
        nodes {
          slug
        }
      }
    }
  `);

  const firstLanguageNavData = allContentfulNavigation.nodes.find(node => node.node_locale.slice(0, 2) === firstLanguageLocalCode);
  const secondLanguageNavData = allContentfulNavigation.nodes.find(node => node.node_locale.slice(0, 2) === secondLanguageUrlPrefix);

  let usedLanguageNavData;
  if (isSecondLanguageOnUrl && secondLanguageNavData) {
    usedLanguageNavData = secondLanguageNavData;
  } else {
    usedLanguageNavData = firstLanguageNavData!;
  }

  const {
    node_locale,
    brandLink,
    mainLinks,
    topLevelLinks,
    languageLink,
    signInLabel,
    signInUrl,
    isEgyptianSite
  } = usedLanguageNavData;

  return (
    <PureNavigation
      node_locale={node_locale}
      mainLinks={mainLinks}
      topLevelLinks={topLevelLinks}
      languageLink={languageLink}
      brandLink={brandLink}
      signInLabel={signInLabel}
      signInUrl={signInUrl}
      isEgyptianSite={isEgyptianSite}
      pageBuilderSlugList={[...new Set(allContentfulPageBuilder.nodes.filter(node => !node.slug.includes("/") && node.slug !== secondLanguageUrlPrefix).map(node => node.slug))]}
      secondLanguageUrlPrefix={secondLanguageUrlPrefix}
      isSecondLanguageOnUrl={isSecondLanguageOnUrl}
    ></PureNavigation>
  );
};

export default Navigation;
